import { POS_INFINITY } from "@pdfking/math"

/**
 * Collects an async generator into an array: all of it, or at most the first
 * `n` values (fewer if it runs out first). Only what is collected is pulled —
 * a generator stopped at `n` stays open, and the next call picks up where this
 * one stopped.
 *
 * @param iterable - The async generator to collect
 * @param n - How many values to collect at most (default: all)
 * @returns A promise that resolves to an array of the collected values
 *
 * @example
 * ```ts
 * import { Collect } from "@pdfking/tfunc"
 *
 * async function* numbers() {
 *   for (let i = 0; i < 5; i++) yield i
 * }
 *
 * await Collect(numbers()) // [0, 1, 2, 3, 4]
 *
 * const gen = numbers()
 * await Collect(gen, 2) // [0, 1]
 * await Collect(gen, 2) // [2, 3]
 * ```
 */
export const Collect = async <T>(iterable: AsyncGenerator<T>, n = POS_INFINITY): Promise<T[]> => {
  const res: T[] = []
  for (let i = 0; i < n; i++) {
    const { done, value }: IteratorResult<T> = await iterable.next()
    if (done) break
    res.push(value)
  }
  return res
}
